import React, { useState, useMemo } from "react";

import { motion, AnimatePresence } from "framer-motion";

import {

  CurrencyRupeeIcon,

  CalculatorIcon,

  ShieldCheckIcon,

  BriefcaseIcon,

  ArrowTrendingUpIcon,

  BanknotesIcon,

  ChartBarIcon,

} from "@heroicons/react/24/outline";



const NewRateTaxCalculator = () => {

  const [activeSection, setActiveSection] = useState(0);



  // Initial State with dummy numeric values

  const [formData, setFormData] = useState({

    // Section 1: Gross Income

    basic: 500000, hra: 200000, specialAllowance: 50000, statutoryBonus: 20000,

    annualChild: 2400, topPerformerBonus: 10000, serviceWeightage: 5000,

    annualH1Bonus: 15000, specialServiceBonus: 8000, holidayPayment: 4000,

    variablePay: 60000, transportAllowance: 19200, progressAllowance: 12000,

    processAllowance: 10000, nightShiftAllowance: 24000, rewardsRecognition: 5000,

    annual2in1Box: 2000, incentive: 30000, panelIncentive: 5000, specialBonus: 15000,

    weekendAllowance: 12000, annualGiftPerq: 5000, annualTelephone: 6000,

    covid19PFRelief: 0, prevEmployerIncome: 0, gratuity: 0, leaveEncashment: 0,

    annualRetiralPerq: 0, oneTimeBonus: 10000, referralBonus: 20000,

    annualReimbursement: 15000, carPerq: 0, annualH2Bonus: 10000, ltaTaxable: 30000,



    // Section 2: Sec 10

    hraExemption: 150000, gratuityExemption: 0, ltaExemption: 25000, leaveEncashmentExemption: 0,



    // Section 3: Sec 16

    standardDeduction: 50000, professionTax: 2400, previousPT: 0,



    // Section 5: 80C

    providentFund: 60000, prevPF: 0, lic: 25000, annuityPlan: 0, ctd: 0,

    fixedDeposit: 0, jeevanPension: 0, nsc: 0, ppf: 50000, tuitionFees: 15000,

    ulip: 0, elss: 0, sukanyaSamriddi: 0, nps80CCD1: 0, hlpr: 0,



    // Section 6: Other

    prevHealthParent: 25000, prevHealthSelf: 5000, sec80DParent: 0,

    npsAdditional: 50000, section80D: 0, disabledDependent: 0, medicalExpensed80DDB: 0,

    permanentDisability: 0, interestEdLoan: 0, addInterestHousing: 0,

    sec80EEA: 0, sec80EEB: 0, npsERDeduction: 0,



    // Section 7: Taxable Income

    otherIncome: 5000, incomeHouseProperty: 0, incHouse: 0, intLossLetout: 0,

  });



  // Calculation Logic

  const calculations = useMemo(() => {

    const grossKeys = [

      'basic', 'hra', 'specialAllowance', 'statutoryBonus', 'annualChild', 'topPerformerBonus',

      'serviceWeightage', 'annualH1Bonus', 'specialServiceBonus', 'holidayPayment', 'variablePay',

      'transportAllowance', 'progressAllowance', 'processAllowance', 'nightShiftAllowance',

      'rewardsRecognition', 'annual2in1Box', 'incentive', 'panelIncentive', 'specialBonus',

      'weekendAllowance', 'annualGiftPerq', 'annualTelephone', 'covid19PFRelief', 'prevEmployerIncome',

      'gratuity', 'leaveEncashment', 'annualRetiralPerq', 'oneTimeBonus', 'referralBonus',

      'annualReimbursement', 'carPerq', 'annualH2Bonus', 'ltaTaxable'

    ];

    const totalGross = grossKeys.reduce((acc, key) => acc + (Number(formData[key]) || 0), 0);



    const totalSec10 = (Number(formData.hraExemption) || 0) + (Number(formData.gratuityExemption) || 0) +

                       (Number(formData.ltaExemption) || 0) + (Number(formData.leaveEncashmentExemption) || 0);



    const totalSec16 = (Number(formData.standardDeduction) || 0) + (Number(formData.professionTax) || 0) +

                       (Number(formData.previousPT) || 0);



    const taxableSalary = totalGross - totalSec10 - totalSec16;



    const total80C = Math.min(150000, [

      'providentFund', 'prevPF', 'lic', 'annuityPlan', 'ctd', 'fixedDeposit', 'jeevanPension',

      'nsc', 'ppf', 'tuitionFees', 'ulip', 'elss', 'sukanyaSamriddi', 'nps80CCD1', 'hlpr'

    ].reduce((acc, key) => acc + (Number(formData[key]) || 0), 0));



    const totalOtherInv = [

      'prevHealthParent', 'prevHealthSelf', 'sec80DParent', 'npsAdditional', 'section80D',

      'disabledDependent', 'medicalExpensed80DDB', 'permanentDisability', 'interestEdLoan',

      'addInterestHousing', 'sec80EEA', 'sec80EEB', 'npsERDeduction'

    ].reduce((acc, key) => acc + (Number(formData[key]) || 0), 0);



    const finalTaxableIncome = taxableSalary + (Number(formData.otherIncome) || 0) - total80C - totalOtherInv;

   

    const dummyOldTax = finalTaxableIncome * 0.20;

    const dummyNewTax = finalTaxableIncome * 0.15;



    return { totalGross, totalSec10, totalSec16, taxableSalary, total80C, totalOtherInv, finalTaxableIncome, dummyOldTax, dummyNewTax };

  }, [formData]);



  const handleInputChange = (e) => {

    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));

  };



  const sections = [

    { id: 0, title: "Gross Income", icon: BriefcaseIcon },

    { id: 1, title: "Sec 10 Exemption", icon: ShieldCheckIcon },

    { id: 2, title: "Sec 16 Exemption", icon: CalculatorIcon },

    { id: 3, title: "Taxable Salary", icon: CurrencyRupeeIcon },

    { id: 4, title: "Rebate Under Sec 80C", icon: ArrowTrendingUpIcon },

    { id: 5, title: "Total Other Investment", icon: BanknotesIcon },

    { id: 6, title: "Taxable Income", icon: ChartBarIcon },

  ];



  const InputField = ({ label, name, value, readOnly = false }) => (

    <div className="flex flex-col space-y-1 font-roboto">

      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</label>

      <input

        type="number"

        name={name}

        value={value}

        onChange={handleInputChange}

        readOnly={readOnly}

        className={`px-4 py-2.5 rounded-lg border text-sm transition-all duration-200 ${

          readOnly

            ? "bg-gray-100 border-gray-200 text-gray-600 font-bold cursor-not-allowed"

            : "bg-white border-gray-300 text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"

        }`}

      />

    </div>

  );



  return (

    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-roboto">

      {/* External Font Links */}

      <style>

        {`

          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&family=Roboto:wgh…

          .font-poppins { font-family: 'Poppins', sans-serif; }

          .font-roboto { font-family: 'Roboto', sans-serif; }

        `}

      </style>



      {/* HEADER BAR */}

      <header className="bg-white border-b border-gray-200 px-8 py-4 sticky top-0 z-30 shadow-sm font-poppins">

        <div className="flex justify-between items-center max-w-[1600px] mx-auto">

          <h1 className="text-xl font-bold text-gray-800 tracking-tight">New Rate Tax Calculator</h1>

          <div className="flex gap-4">

            <div className="bg-emerald-50 border border-emerald-200 px-6 py-2 rounded-xl text-center">

              <p className="text-[10px] uppercase font-bold text-emerald-600">Old Tax Regime</p>

              <p className="text-lg font-black text-emerald-800">₹{calculations.dummyOldTax.toLocaleString()}</p>

            </div>

            <div className="bg-emerald-600 px-6 py-2 rounded-xl text-center shadow-lg shadow-emerald-200">

              <p className="text-[10px] uppercase font-bold text-emerald-100">New Tax Regime</p>

              <p className="text-lg font-black text-white">₹{calculations.dummyNewTax.toLocaleString()}</p>

            </div>

          </div>

        </div>

      </header>



      <div className="flex flex-1 max-w-[1600px] mx-auto w-full p-6 gap-6">

        {/* SIDEBAR */}

        <aside className="w-72 flex flex-col space-y-2 font-poppins">

          {sections.map((sec) => (

            <button

              key={sec.id}

              onClick={() => setActiveSection(sec.id)}

              className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 text-left ${

                activeSection === sec.id

                  ? "bg-white text-emerald-700 shadow-md border-l-4 border-emerald-600 font-bold"

                  : "text-gray-500 hover:bg-gray-100 font-medium"

              }`}

            >

              <sec.icon className={`h-5 w-5 ${activeSection === sec.id ? "text-emerald-600" : "text-gray-400"}`} />

              <span className="text-sm">{sec.title}</span>

            </button>

          ))}

        </aside>



        {/* MAIN CONTENT AREA */}

        <main className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">

          <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 font-poppins">

            <h2 className="text-2xl font-extrabold text-gray-800">{sections[activeSection].title}</h2>

            <span className="text-xs font-bold text-gray-400 tracking-widest">SECTION {activeSection + 1} OF 7</span>

          </div>



          <div className="p-8 overflow-y-auto max-h-[calc(100vh-280px)]">

            <AnimatePresence mode="wait">

              <motion.div

                key={activeSection}

                initial={{ opacity: 0, y: 10 }}

                animate={{ opacity: 1, y: 0 }}

                exit={{ opacity: 0, y: -10 }}

                transition={{ duration: 0.2 }}

                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"

              >

                {/* Sections Rendered Here using InputField (Roboto Font) */}

                {activeSection === 0 && (

                  <>

                    <InputField label="Basic Salary" name="basic" value={formData.basic} />

                    <InputField label="HRA" name="hra" value={formData.hra} />

                    <InputField label="Special Allowance" name="specialAllowance" value={formData.specialAllowance} />

                    <InputField label="Statutory Bonus" name="statutoryBonus" value={formData.statutoryBonus} />

                    <InputField label="Annual Child Allowance" name="annualChild" value={formData.annualChild} />

                    <InputField label="Top Performer Bonus" name="topPerformerBonus" value={formData.topPerformerBonus} />

                    <InputField label="Service Weightage Award" name="serviceWeightage" value={formData.serviceWeightage} />

                    <InputField label="Annual H1 Bonus" name="annualH1Bonus" value={formData.annualH1Bonus} />

                    <InputField label="Special Service Bonus" name="specialServiceBonus" value={formData.specialServiceBonus} />

                    <InputField label="Holiday Payment" name="holidayPayment" value={formData.holidayPayment} />

                    <InputField label="Variable Pay" name="variablePay" value={formData.variablePay} />

                    <InputField label="Transport Allowance" name="transportAllowance" value={formData.transportAllowance} />

                    <InputField label="Progress Allowance" name="progressAllowance" value={formData.progressAllowance} />

                    <InputField label="Process Allowance" name="processAllowance" value={formData.processAllowance} />

                    <InputField label="Night Shift Allowance" name="nightShiftAllowance" value={formData.nightShiftAllowance} />

                    <InputField label="Rewards Recognition" name="rewardsRecognition" value={formData.rewardsRecognition} />

                    <InputField label="Annual 2 in 1 Box" name="annual2in1Box" value={formData.annual2in1Box} />

                    <InputField label="Incentive" name="incentive" value={formData.incentive} />

                    <InputField label="Panel Incentive" name="panelIncentive" value={formData.panelIncentive} />

                    <InputField label="Special Bonus" name="specialBonus" value={formData.specialBonus} />

                    <InputField label="Weekend Allowance" name="weekendAllowance" value={formData.weekendAllowance} />

                    <InputField label="Annual Gift Perq" name="annualGiftPerq" value={formData.annualGiftPerq} />

                    <InputField label="Annual Telephone" name="annualTelephone" value={formData.annualTelephone} />

                    <InputField label="Covid-19 PF Relief" name="covid19PFRelief" value={formData.covid19PFRelief} />

                    <InputField label="Prev. Employer Income" name="prevEmployerIncome" value={formData.prevEmployerIncome} />

                    <InputField label="Gratuity" name="gratuity" value={formData.gratuity} />

                    <InputField label="Leave Encashment" name="leaveEncashment" value={formData.leaveEncashment} />

                    <InputField label="Annual Retiral Perq" name="annualRetiralPerq" value={formData.annualRetiralPerq} />

                    <InputField label="One Time Bonus" name="oneTimeBonus" value={formData.oneTimeBonus} />

                    <InputField label="Referral Bonus" name="referralBonus" value={formData.referralBonus} />

                    <InputField label="Reimbursement Expenses" name="annualReimbursement" value={formData.annualReimbursement} />

                    <InputField label="Car Perq" name="carPerq" value={formData.carPerq} />

                    <InputField label="Annual H2 Bonus" name="annualH2Bonus" value={formData.annualH2Bonus} />

                    <InputField label="LTA Taxable" name="ltaTaxable" value={formData.ltaTaxable} />

                    <div className="lg:col-span-3 mt-4 border-t pt-6">

                      <InputField label="Gross Income (Total)" name="totalGross" value={calculations.totalGross} readOnly />

                    </div>

                  </>

                )}



                {activeSection === 1 && (

                  <>

                    <InputField label="HRA Exemption" name="hraExemption" value={formData.hraExemption} />

                    <InputField label="Gratuity Exemption" name="gratuityExemption" value={formData.gratuityExemption} />

                    <InputField label="LTA Exemption" name="ltaExemption" value={formData.ltaExemption} />

                    <InputField label="Leave Encashment Exemption" name="leaveEncashmentExemption" value={formData.leaveEncashmentExemption} />

                    <div className="lg:col-span-3 border-t pt-6"><InputField label="Sec 10 Exemption (Total)" value={calculations.totalSec10} readOnly /></div>

                  </>

                )}



                {activeSection === 2 && (

                  <>

                    <InputField label="Standard Deduction" name="standardDeduction" value={formData.standardDeduction} />

                    <InputField label="Profession Tax" name="professionTax" value={formData.professionTax} />

                    <InputField label="Previous PT" name="previousPT" value={formData.previousPT} />

                    <div className="lg:col-span-3 border-t pt-6"><InputField label="Sec 16 Exemption (Total)" value={calculations.totalSec16} readOnly /></div>

                  </>

                )}



                {activeSection === 3 && (

                  <>

                    <InputField label="Salary After Section 10" value={calculations.totalGross - calculations.totalSec10} readOnly />

                    <InputField label="Salary After Section 16" value={calculations.totalGross - calculations.totalSec16} readOnly />

                    <InputField label="Taxable Salary" value={calculations.taxableSalary} readOnly />

                  </>

                )}



                {activeSection === 4 && (

                  <>

                    <InputField label="Provident Fund" name="providentFund" value={formData.providentFund} />

                    <InputField label="Previous PF" name="prevPF" value={formData.prevPF} />

                    <InputField label="LIC" name="lic" value={formData.lic} />

                    <InputField label="Annuity Plan" name="annuityPlan" value={formData.annuityPlan} />

                    <InputField label="CTD" name="ctd" value={formData.ctd} />

                    <InputField label="Fixed Deposit" name="fixedDeposit" value={formData.fixedDeposit} />

                    <InputField label="Jeevan Pension" name="jeevanPension" value={formData.jeevanPension} />

                    <InputField label="NSC" name="nsc" value={formData.nsc} />

                    <InputField label="PPF" name="ppf" value={formData.ppf} />

                    <InputField label="Tuition Fees" name="tuitionFees" value={formData.tuitionFees} />

                    <InputField label="ULIP" name="ulip" value={formData.ulip} />

                    <InputField label="ELSS" name="elss" value={formData.elss} />

                    <InputField label="Sukanya Samriddi Account" name="sukanyaSamriddi" value={formData.sukanyaSamriddi} />

                    <InputField label="NPS 80CCD1" name="nps80CCD1" value={formData.nps80CCD1} />

                    <InputField label="HLPR" name="hlpr" value={formData.hlpr} />

                    <div className="lg:col-span-3 border-t pt-6"><InputField label="Total 80C Investments (Max 1.5L)" value={calculations.total80C} readOnly /></div>

                  </>

                )}



                {activeSection === 5 && (

                  <>

                    <InputField label="Prev Health Parent" name="prevHealthParent" value={formData.prevHealthParent} />

                    <InputField label="Prev Health Self" name="prevHealthSelf" value={formData.prevHealthSelf} />

                    <InputField label="SEC 80D Parent" name="sec80DParent" value={formData.sec80DParent} />

                    <InputField label="NPS Additional" name="npsAdditional" value={formData.npsAdditional} />

                    <InputField label="Section 80D" name="section80D" value={formData.section80D} />

                    <InputField label="Disabled Dependent 80DD" name="disabledDependent" value={formData.disabledDependent} />

                    <InputField label="Medical Expenses 80DDB" name="medicalExpensed80DDB" value={formData.medicalExpensed80DDB} />

                    <InputField label="Physical Disability" name="permanentDisability" value={formData.permanentDisability} />

                    <InputField label="Interest Education Loan" name="interestEdLoan" value={formData.interestEdLoan} />

                    <InputField label="Add. Interest Housing" name="addInterestHousing" value={formData.addInterestHousing} />

                    <InputField label="80EEA Home Loan" name="sec80EEA" value={formData.sec80EEA} />

                    <InputField label="80EEB EV Loan" name="sec80EEB" value={formData.sec80EEB} />

                    <InputField label="NPS ER Deduction" name="npsERDeduction" value={formData.npsERDeduction} />

                    <div className="lg:col-span-3 border-t pt-6"><InputField label="Total Other Investment" value={calculations.totalOtherInv} readOnly /></div>

                  </>

                )}



                {activeSection === 6 && (

                  <>

                    <InputField label="Other Income" name="otherIncome" value={formData.otherIncome} />

                    <InputField label="Income House Property" name="incomeHouseProperty" value={formData.incomeHouseProperty} />

                    <InputField label="INC_HOUSE" name="incHouse" value={formData.incHouse} />

                    <InputField label="Interest Loss Letout" name="intLossLetout" value={formData.intLossLetout} />

                    <InputField label="Taxable Salary" value={calculations.taxableSalary} readOnly />

                    <div className="lg:col-span-3 border-t pt-6">

                      <div className="bg-slate-900 p-8 rounded-2xl text-white font-poppins shadow-xl shadow-slate-200">

                        <p className="text-xs text-emerald-400 font-bold uppercase tracking-[0.2em]">Net Taxable Income</p>

                        <p className="text-5xl font-black mt-3 tracking-tight">₹{calculations.finalTaxableIncome.toLocaleString()}</p>

                      </div>

                    </div>

                  </>

                )}

              </motion.div>

            </AnimatePresence>

          </div>

         

          <div className="mt-auto p-6 bg-gray-50 border-t border-gray-100 flex justify-end space-x-4 font-poppins">

            <button

                disabled={activeSection === 0}

                onClick={() => setActiveSection(s => s - 1)}

                className="px-6 py-2 text-sm font-bold text-gray-400 hover:text-gray-800 disabled:opacity-30 transition-colors"

            >

              Previous

            </button>

            <button

                disabled={activeSection === 6}

                onClick={() => setActiveSection(s => s + 1)}

                className="px-8 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all disabled:opacity-30"

            >

              Next Section

            </button>

          </div>

        </main>

      </div>

    </div>

  );

};



export default NewRateTaxCalculator;